// src/components/NotificationBell.jsx
// The notification bell (3.56.0, the owner's design of Oct 11). It sits right
// of the menu bar, mirroring the client switcher on the left (phones: top
// right, by your picture). Every fax pop-up also waits here for 7 days,
// including what happened while you were away (the server keeps the list,
// backend /portal/bell). The number is how many are waiting. Click the bell
// for the list (3.56.2, owner: on a computer, just point at the bell), newest first: open one (it opens the fax and leaves the
// list), swipe one sideways or press ✕ to clear it, or Mark all as seen. It
// follows your pop-up choices in Preferences and your role, like the pop-ups.
// 3.59.2 (owner): the pop-ups of fax actions and people actions you take
// ("Fax cancelled", "Invite sent to …") are kept here too, on your account,
// for 7 days: listed with the rest, but not in the number (you saw them pop up).
import { useCallback, useEffect, useRef, useState } from 'react';
import Emo from './Emo';
import { reportClientError } from './ErrorBoundary';
import { fetchBell, clearBell, addBellAlert, fetchNextReminder } from '../utils/api';
import { noticeFor } from '../hooks/useFaxNotices';
import { timeZone } from '../utils/timeZone';
import { usePreferences } from '../hooks/usePreferences';
import { useNotify } from './Notifications';

const SWIPE_AWAY = 80;   // px sideways that clears a notification
const HOVER_OPEN = 150, HOVER_CLOSE = 400;   // ms: pointing opens, moving away closes
const canHover = () => typeof window !== 'undefined' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

function when(iso) {
  const t = Date.parse(iso || ''); if (!t) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const opts = { timeZone: timeZone(), hour: 'numeric', minute: '2-digit' };
  const sameDay = new Date(t).toLocaleDateString('en-US', { timeZone: timeZone() }) === new Date().toLocaleDateString('en-US', { timeZone: timeZone() });
  return sameDay ? new Date(t).toLocaleTimeString('en-US', opts) : new Date(t).toLocaleString('en-US', { ...opts, weekday: 'short', month: 'short', day: 'numeric' });
}

export default function NotificationBell({ getToken, nudge = 0, showClient = false, onOpen, phone = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);       // null = not loaded yet
  const [alerts, setAlerts] = useState([]);       // 3.59.2: your own fax and people actions
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null), btnRef = useRef(null);
  // 3.56.2 (owner): on a computer the list opens when you point at the bell and
  // closes a moment after you move away; click, tap and the keyboard still work
  const hoverT = useRef(null), hoverOpenedAt = useRef(0);
  const hoverable = !phone && canHover();
  const onEnter = () => {
    if (!hoverable) return;
    clearTimeout(hoverT.current);
    if (!open) hoverT.current = setTimeout(() => { hoverOpenedAt.current = Date.now(); setOpen(true); }, HOVER_OPEN);
  };
  const onLeave = () => {
    if (!hoverable) return;
    clearTimeout(hoverT.current);
    hoverT.current = setTimeout(() => setOpen(false), HOVER_CLOSE);
  };
  useEffect(() => () => clearTimeout(hoverT.current), []);
  // a click right after pointing opened it keeps it open (it doesn't close it again)
  const onBtnClick = () => {
    clearTimeout(hoverT.current);
    if (open && Date.now() - hoverOpenedAt.current < 1200) return;
    hoverOpenedAt.current = 0;
    setOpen(o => !o);
  };

  // 3.56.5 (owner): "Pop-up notices for fax updates" off turns the bell off too (the server
  // sends an empty list); unticking only some kinds leaves just those out
  const { prefs } = usePreferences();
  const off = prefs.faxNotices === false;
  const kindsKey = JSON.stringify(prefs.popupKinds || {});
  // 3.62.2: alerts kept by the server come back without their "alert" mark;
  // without it a row was drawn as a fax notice, found none and blanked the page
  const load = useCallback(() => fetchBell(getToken).then(r => { setItems(r.items || []); setAlerts((r.alerts || []).map(a => ({ ...a, alert: true }))); }).catch(() => {}), [getToken]);
  // A tagged pop-up (components/Notifications.jsx): listed at once, kept on the account
  useEffect(() => {
    const onAlert = e => {
      const a = e.detail || {};
      if (!a.title) return;
      const item = { key: `alert:${Date.now()}:${Math.random().toString(36).slice(2, 7)}`, alert: true, group: a.group, tone: a.tone, title: a.title, detail: a.detail || '', at: new Date().toISOString() };
      setAlerts(list => [item, ...list].slice(0, 100));
      addBellAlert(item, getToken).catch(() => {});
    };
    window.addEventListener('bell-alert', onAlert);
    return () => window.removeEventListener('bell-alert', onAlert);
  }, [getToken]);
  // On load, when a pop-up arrives (nudge), when your pop-up choices change, and when the bell opens
  useEffect(() => { load(); }, [load, nudge, off, kindsKey]);
  useEffect(() => { if (open) load(); }, [open, load]);
  // 3.74.2 (owner): reminders show in the portal as soon as they're sent.
  // A phone/computer notification arriving (the app's background helper,
  // public/sw.js) checks the bell at once; and when a reminder is coming up,
  // the bell checks again 30 seconds after the server's run that sends it
  // (3.74.3: every minute; it was the 5-minute job), for devices without
  // notifications.
  useEffect(() => {
    const onMsg = e => { if (e.data?.type === 'push') load(); };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', onMsg);
  }, [load]);
  const [nextReminder, setNextReminder] = useState(null);
  const loadNext = useCallback(() => fetchNextReminder(getToken).then(r => setNextReminder(r.at || null)).catch(() => {}), [getToken]);
  useEffect(() => { loadNext(); }, [loadNext]);
  useEffect(() => { window.addEventListener('notes-changed', loadNext); return () => window.removeEventListener('notes-changed', loadNext); }, [loadNext]);
  useEffect(() => {
    const due = Date.parse(nextReminder || '');
    if (!due) return undefined;
    const MIN = 60 * 1000;
    const at = Math.max(Math.ceil(due / MIN) * MIN, Math.ceil(Date.now() / MIN) * MIN) + 30 * 1000;
    if (at - Date.now() > 24 * 3600 * 1000) return undefined;   // far off: checked again on the next load
    const t = setTimeout(() => { load(); loadNext(); }, at - Date.now());
    return () => clearTimeout(t);
  }, [nextReminder, load, loadNext]);
  // A slow refresh in case nothing else triggers one (another device cleared it)
  useEffect(() => { const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 5 * 60 * 1000); return () => clearInterval(t); }, [load]);
  // 3.56.4 (owner): the app icon shows the bell's number (it was the Inbox count, 3.52.1),
  // where the device shows one; the app's background helper keeps it for push notifications
  // 3.74.0: My notes reminders are counted too (they're for you to act on)
  const reminders = alerts.filter(a => a.group === 'notes');
  const shownCount = items === null ? null : (off ? 0 : items.length) + reminders.length;
  useEffect(() => {
    if (shownCount === null) return;
    try { if (navigator.setAppBadge) { if (shownCount > 0) navigator.setAppBadge(shownCount).catch(() => {}); else navigator.clearAppBadge?.().catch(() => {}); } } catch { /* not supported */ }
    try { navigator.serviceWorker?.controller?.postMessage({ type: 'badge', count: shownCount, off }); } catch { /* no helper */ }
  }, [shownCount, off]);

  // Close on a click outside or Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } };
    document.addEventListener('mousedown', onDown); document.addEventListener('touchstart', onDown); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('touchstart', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const clearOne = useCallback(key => {
    setItems(list => (list || []).filter(i => i.key !== key));
    setAlerts(list => list.filter(i => i.key !== key));
    clearBell({ key }, getToken).catch(() => load());
  }, [getToken, load]);
  const clearAll = async () => {
    setBusy(true); setItems([]); setAlerts([]);
    try { await clearBell({ all: true }, getToken); } catch { load(); }
    setBusy(false);
  };
  const openItem = it => {
    clearOne(it.key);
    setOpen(false);
    onOpen?.(it.direction === 'outgoing' ? 'outgoing' : 'incoming', it.faxId);
  };

  const count = (off ? 0 : items?.length || 0) + reminders.length;
  // 3.74.0: a reminder that arrives while the portal is open also pops up once
  const { notify } = useNotify();
  const seenReminders = useRef(null);
  useEffect(() => {
    const keys = reminders.map(r => r.key);
    if (seenReminders.current === null) { if (items !== null) seenReminders.current = new Set(keys); return; }
    reminders.filter(r => !seenReminders.current.has(r.key)).forEach(r => notify({ tone: 'info', icon: '📝', title: r.title, detail: r.detail || 'From My notes', hint: 'Open the note ›',
      onClick: () => { window.dispatchEvent(new CustomEvent('open-notes', { detail: { noteId: r.noteId } })); clearOne(r.key); } }));
    keys.forEach(k => seenReminders.current.add(k));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminders.map(r => r.key).join(','), items === null]);
  // 3.62.7 (owner): the new bell rings (3 swings, sound arcs) when new
  // notifications arrive, not on the first load; it still rings on hover
  const [ring, setRing] = useState(false);
  const lastCount = useRef(null);
  useEffect(() => {
    if (items === null) return undefined;
    const before = lastCount.current;
    lastCount.current = count;
    if (before === null || count <= before) return undefined;
    setRing(true);
    const t = setTimeout(() => setRing(false), 4200);
    return () => clearTimeout(t);
  }, [count, items]);
  // Fax news and your own actions together, newest first (fax news hidden while fax pop-ups are off)
  const rows = [...(off ? [] : items || []), ...alerts].sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
  return (
    <div ref={wrapRef} style={S.wrap} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <style>{CSS}</style>
      <button ref={btnRef} type="button" className="bell-btn" onClick={onBtnClick} aria-expanded={open} aria-haspopup="dialog"
        aria-label={count ? `Notifications, ${count} new` : 'Notifications'} title="Notifications" style={{ ...S.btn, ...(phone ? S.btnPhone : {}) }}>
        {/* 3.62.18 (owner): while there are notifications the bell keeps ringing (its swing and sound waves) until they're cleared */}
        <style>{RINGING_CSS}</style>
        <Emo e="🔔" className={count > 0 ? 'bell-ringing' : ring ? 'bell-ring' : ''} style={{ fontSize: phone ? 22 : 24 }} />
        {count > 0 && <span style={S.count} aria-hidden="true">{count > 99 ? '99+' : count}</span>}
      </button>
      {open && (
        <div role="dialog" aria-label="Notifications" style={{ ...S.panel, ...(phone ? S.panelPhone : {}) }} className="bell-panel">
          <div style={S.head}>
            <strong style={S.title}><Emo e="🔔" /> Notifications</strong>
            <button type="button" onClick={clearAll} disabled={!rows.length || busy} style={{ ...S.allBtn, opacity: rows.length ? 1 : 0.5 }}>✓ Mark all as seen</button>
          </div>
          <div style={S.list}>
            {off && <div style={{ ...S.empty, padding: '14px 12px' }}><div>Fax pop-ups are turned off.</div><div style={S.hint}>Turn on Pop-up notices for fax updates in Preferences to see your fax updates here.</div></div>}
            {!off && items === null && <div style={S.empty}>Loading…</div>}
            {items !== null && !rows.length && !off && <div style={S.empty}><Emo e="🎉" style={{ fontSize: 26 }} /><div>You’re all caught up.</div><div style={S.hint}>New fax updates show here for 7 days.</div></div>}
            {rows.map(it => (it.alert
              ? <Row key={it.key} it={it} alert onOpen={() => { if (it.group === 'notes') { window.dispatchEvent(new CustomEvent('open-notes', { detail: { noteId: it.noteId } })); setOpen(false); } clearOne(it.key); }} onClear={() => clearOne(it.key)} />
              : <Row key={it.key} it={it} showClient={showClient} onOpen={() => openItem(it)} onClear={() => clearOne(it.key)} />))}
          </div>
          {rows.length > 0 && <div style={S.foot}>{phone ? 'Swipe one sideways to clear it.' : 'Swipe one sideways, or press ✕, to clear it.'} Opening one clears it too.</div>}
        </div>
      )}
    </div>
  );
}

// One notification: click to open, drag sideways (finger, trackpad or mouse) to clear
// 3.59.2: alert = one of your own actions (not counted; clicking it clears it)
function Row({ it, showClient, onOpen, onClear, alert = false }) {
  const n = alert ? { icon: it.group === 'people' ? '👤' : it.group === 'notes' ? '📝' : '📠', title: it.title, detail: it.detail, tone: it.tone === 'error' ? 'error' : 'info' } : noticeFor(it, { showClient });
  const reminder = alert && it.group === 'notes';   // 3.74.0: a My notes reminder
  const [dx, setDx] = useState(0);
  const [leaving, setLeaving] = useState(0);
  const start = useRef(null), moved = useRef(false);
  // 3.74.3: the pointer is captured only once a sideways swipe starts; captured
  // on press, Chrome sent the click to the row, not its button, so a click
  // on a fax or a reminder in the bell opened nothing
  const down = e => { start.current = { x: e.clientX, y: e.clientY }; moved.current = false; };
  const move = e => {
    if (!start.current) return;
    const x = e.clientX - start.current.x, y = e.clientY - start.current.y;
    if (!moved.current && Math.abs(x) > 6 && Math.abs(x) > Math.abs(y)) { moved.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); }
    if (moved.current) setDx(x);
  };
  const up = () => {
    if (!start.current) return;
    start.current = null;
    if (moved.current && Math.abs(dx) > SWIPE_AWAY) { setLeaving(dx > 0 ? 1 : -1); setTimeout(onClear, 180); return; }
    setDx(0);
  };
  // 3.62.2: a kind this version doesn't know is skipped (never a blank page) and reported once
  const unknown = !n;
  useEffect(() => { if (unknown) reportClientError('bell-skip', `Bell skipped an alert of kind "${it.kind || '(none)'}"`, { alertKind: it.kind || null }); }, [unknown, it.kind]);
  if (!n) return null;
  const tone = { success: 'var(--c-2e7d32)', warning: 'var(--c-ef6c00)', error: 'var(--c-c62828)', info: 'var(--c-00897b)' }[n.tone] || 'var(--c-00897b)';
  return (
    <div className="bell-row" style={{ ...S.row, borderLeftColor: tone, transform: leaving ? `translateX(${leaving * 110}%)` : `translateX(${dx}px)`,
      opacity: leaving ? 0 : 1 - Math.min(0.6, Math.abs(dx) / 300), transition: start.current ? 'none' : 'transform .18s ease, opacity .18s ease' }}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
      <button type="button" style={S.rowMain} onClick={() => { if (!moved.current) onOpen(); }} title={reminder ? 'Open this note in My notes' : alert ? 'Something you did (not counted in the bell’s number). Click to clear it' : 'Open this fax'}>
        <span style={S.rowIcon}><Emo e={n.icon} /></span>
        <span style={S.rowText}>
          <span style={S.rowTitle}>{n.title}</span>
          {n.detail && <span style={S.rowDetail}>{n.detail}</span>}
          <span style={S.rowWhen}>{reminder ? <>Reminder · {when(it.at)}</> : alert ? <>You · {when(it.at)}</> : when(it.at)}</span>
        </span>
      </button>
      {/* 3.56.6 (owner): the ✕ press stays its own (the row's swipe used to capture it) */}
      <button type="button" className="bell-x" onPointerDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); onClear(); }} aria-label={`Clear: ${n.title}`} title="Clear" style={S.x}>✕</button>
    </div>
  );
}

const CSS = `
.bell-btn:hover, .bell-btn:focus-visible { transform: scale(1.06); }
.bell-row { touch-action: pan-y; }
.bell-row .bell-x { opacity: 0; transition: opacity .15s; }
.bell-row:hover .bell-x, .bell-row .bell-x:focus-visible { opacity: 1; }
@media (hover: none) { .bell-row .bell-x { opacity: .6; } }
`;
// The bell's own ring (fluxIcons.js .bell-ring: 3 swings) repeated for as long as
// there are notifications; still for anyone who asks for less motion
const RINGING_CSS = '.bell-ringing .fx-bell .k1{animation:fx-bell-k1 1.3s ease-in-out 0s infinite}.bell-ringing .fx-bell .k2{animation:fx-bell-k2 1.3s ease-in-out 0s infinite}'
  + '.bell-ringing .fx-bell .k3{animation:fx-bell-k3 1.3s ease-in-out 0s infinite}.bell-ringing .fx-bell .k4{animation:fx-bell-k4 1.3s ease-in-out 0s infinite}'
  + '@media (prefers-reduced-motion:reduce){.bell-ringing .fx-bell *{animation:none!important}}';

const S = {
  wrap: { position: 'relative', display: 'inline-flex' },
  btn: { position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 46, height: 46, borderRadius: '50%',
    border: '1.5px solid var(--b-a5d6a7)', background: 'rgb(var(--glass-rgb) / 0.85)', boxShadow: '0 4px 14px rgb(var(--c-00695c-rgb) / 0.12)', cursor: 'pointer', transition: 'transform .15s' },
  btnPhone: { width: 40, height: 40, boxShadow: 'none' },
  // 3.62.18 (owner): the number's red box at 60% of its old size (was 20 px high, 11 px text, 2 px border)
  count: { position: 'absolute', top: -2, right: -2, minWidth: 12, height: 12, padding: '0 2px', borderRadius: 7, background: 'var(--c-d32f2f)', color: 'var(--t-ffffff)',
    fontSize: 8, fontWeight: 800, lineHeight: '12px', textAlign: 'center', border: '1.5px solid var(--c-ffffff)', boxSizing: 'content-box' },
  panel: { position: 'absolute', top: 'calc(100% + 10px)', right: -8, width: 380, maxHeight: '70vh', display: 'flex', flexDirection: 'column', zIndex: 1200,
    background: 'var(--c-ffffff)', border: '1px solid var(--b-c8e6c9)', borderRadius: 14, boxShadow: '0 18px 48px rgb(var(--c-000000-rgb) / 0.22)', overflow: 'hidden' },
  panelPhone: { position: 'fixed', top: 64, left: 8, right: 8, width: 'auto', maxHeight: 'calc(100vh - 64px - var(--cp-bottom-bar, 0px) - 16px)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--c-eceff1)' },
  title: { fontSize: 15, color: 'var(--t-00695c)', display: 'inline-flex', alignItems: 'center', gap: 6 },
  allBtn: { padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--b-c8e6c9)', background: 'transparent', color: 'var(--t-00695c)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  list: { overflowY: 'auto', overflowX: 'hidden', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '28px 12px', color: 'var(--t-546e7a)', fontSize: 14, textAlign: 'center' },
  hint: { fontSize: 12, color: 'var(--t-90a4ae)' },
  row: { position: 'relative', display: 'flex', alignItems: 'stretch', background: 'var(--c-f5f7f8)', borderRadius: 10, borderLeft: '4px solid', userSelect: 'none' },
  rowMain: { flex: 1, minWidth: 0, display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 6px 10px 10px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', color: 'inherit' },
  rowIcon: { fontSize: 20, lineHeight: 1, paddingTop: 2 },
  rowText: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  rowTitle: { fontSize: 13.5, fontWeight: 700, color: 'var(--t-263238)', overflowWrap: 'anywhere' },
  rowDetail: { fontSize: 12.5, color: 'var(--t-546e7a)', overflowWrap: 'anywhere' },
  rowWhen: { fontSize: 11.5, color: 'var(--t-90a4ae)' },
  x: { alignSelf: 'center', margin: '0 6px', width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'none', color: 'var(--t-78909c)', fontSize: 14, cursor: 'pointer' },
  foot: { padding: '8px 14px 10px', fontSize: 11.5, color: 'var(--t-90a4ae)', borderTop: '1px solid var(--c-eceff1)' },
};
